import _ from 'underscore';
import type { Flight, FlightStatus } from './index.d';
import { FLIGHT_STATUS } from './constants';
import { parseFlightsJson } from './parse-flights-from-context';
import { isFlightMatch, getFlightUrlOverrideStatus } from './utils';
import { createFlightInstrumentation } from './create-flight-instrumentation';
import type { FlightInstrumentation } from './create-flight-instrumentation';

export interface GetCustomerFlightStatusSyncParams {
  /**
   * Raw flight-allocations JSON string, typically `serverContext.CustomerFlightAllocations`.
   * Callers extract just this field so this function does not need to know the ServerContext shape.
   */
  flightsJson: string | null | undefined;
  /** Treatment flight ID and/or name. Either is sufficient to match; both may be supplied. */
  treatmentFlightId?: number;
  controlFlightId?: number;
  treatmentFlightName?: string;
  controlFlightName?: string;
  /**
   * Optional instrumentation sink. When provided, emits the same
   * `<scenario>` / `<scenario>-NoExperiment` / `<scenario>-All` traces as `useCustomerFlights`.
   * Defaults to a no-op so non-React callers need not supply one.
   */
  instrumentation?: FlightInstrumentation;
  /** Scenario name for the emitted activities; defaults to `'CustomerFlights'` to match `useCustomerFlights`. */
  scenarioName?: string;
}

/**
 * Synchronously reads the customer (CID-level) flight status from a flight-allocations JSON string
 * (typically hydrated server-side onto `serverContext.CustomerFlightAllocations`).
 * The non-hook counterpart to `useCustomerFlights` for callers outside the React tree.
 * No fetch; honors the `?flights=` URL override; never returns `Loading` (absent
 * allocations resolve to `NotInFlight`).
 *
 * Logging is at parity with `useCustomerFlights`: the `-All` activity traces the parsed
 * allocations on every call, and (when no URL override is active) the flight / non-flight
 * activity traces the matched flight or the requested flight ids.
 *
 * @param {GetCustomerFlightStatusSyncParams} params - Flight-allocations JSON + treatment/control flight id(s)/name(s).
 * @returns {FlightStatus} The resolved flight status (`InTreatment`, `InControl`, or `NotInFlight`).
 */
export const getCustomerFlightStatusSync = ({
  flightsJson,
  treatmentFlightId,
  controlFlightId,
  treatmentFlightName,
  controlFlightName,
  instrumentation,
  scenarioName = 'CustomerFlights',
}: GetCustomerFlightStatusSyncParams): FlightStatus => {
  const { flightActivity, nonFlightActivity, allFlightActivity } =
    createFlightInstrumentation(instrumentation, scenarioName);

  // support flight url override (e.g. ?flights=123,myFlightName) in non-prod
  const urlOverride = getFlightUrlOverrideStatus(treatmentFlightId, treatmentFlightName);

  // Parse (and trace via the -All activity) the raw allocations regardless of override — mirrors
  // useCustomerFlights, whose getFlightsPromise (and its -All trace) runs even when an override is set.
  const flights = parseFlightsJson({ flightsJson, allFlightActivity }) ?? [];

  const experimentFlight = _.find(flights, (f: Flight): boolean =>
    isFlightMatch(f, treatmentFlightId, treatmentFlightName) ||
    isFlightMatch(f, controlFlightId, controlFlightName));

  // Match / non-match logging is skipped while a URL override is active, matching
  // useCustomerFlights' `urlOverride === null` log gate.
  if (urlOverride === null) {
    if (experimentFlight) {
      flightActivity.trace(JSON.stringify(experimentFlight));
    } else {
      nonFlightActivity.trace(JSON.stringify({ treatmentFlightId, controlFlightId }));
    }
  }

  if (urlOverride !== null) {
    return urlOverride;
  }

  if (isFlightMatch(experimentFlight, treatmentFlightId, treatmentFlightName)) {
    return FLIGHT_STATUS.IN_TREATMENT;
  }
  if (isFlightMatch(experimentFlight, controlFlightId, controlFlightName)) {
    return FLIGHT_STATUS.IN_CONTROL;
  }

  return FLIGHT_STATUS.NOT_IN_FLIGHT;
};
